import type { SourceEvent, SourceId } from '../api/calendar';
import { SOURCE_META } from '../connectors/registry';

// What the source knows about an item, shaped for display (Items 7 + 12,
// ux-batch-2026-07). A needs-input card shows a subject and why it was held;
// these are the facts that let the user decide — a ticket's tags and number, a
// meeting's attendance and invitation reply.
//
// Two models, because they render in two places. The link is the item's address
// in the source, so it rides with the timestamp and the source badge. The facts
// row is then one thing only: what might settle the attribution.
//
// Within the row, weight follows consequence. `quiet` facts are informational
// and render as one dotted clause in the voice of the timestamp; `flags` are
// state that changes whether the time is the user's at all, and each keeps a
// chip. Everything having a chip is what made them all unreadable.

export interface SourceLink {
  href: string;
  // The source's own item number, when it has one — rendered as text. Null for
  // a source with nothing to name (an Outlook event), which renders an icon.
  ref: string | null;
  // Always set: the icon's accessible name, and the text link's tooltip.
  title: string;
}

// One contributing event, shaped for the rundowns that say where a cell's or a
// row's time came from. Everything past the id and hours comes from the live
// read, so an event that no longer resolves still lists with its hours.
export interface SourceEventRef {
  eventId: string;
  hours: number;
  subject?: string;
  start?: string;
  source?: SourceId;
  link?: SourceLink | null;
}

export interface SourceFacts {
  tags: string[];
  // Tags cut by the card cap; 0 when the list is whole.
  tagOverflow: number;
  quiet: string[];
  flags: string[];
}

const CARD_TAG_CAP = 3;

// A response chip only appears when the reply says the user might not have been
// there. 'none' is not a non-answer: Graph returns it for an appointment with no
// attendees, so a solo focus block would otherwise read as unanswered.
const RESPONSE_FLAGS: Record<string, string> = {
  declined: 'Declined',
  notResponded: 'Not responded',
};

// Tentative is a real answer, so it reads as information rather than a warning.
const RESPONSE_QUIET: Record<string, string> = {
  tentativelyAccepted: 'Tentative',
};

// 'Held, not worked' — the two availability values that argue against drafting
// the time. busy/tentative/workingElsewhere say nothing worth showing.
const AVAILABILITY_FLAGS: Record<string, string> = {
  free: 'Free',
  oof: 'Out of office',
};

/** The item's address in the source, or null when the source gave us no URL. */
export function sourceLink(event: SourceEvent | undefined): SourceLink | null {
  if (!event?.sourceItemUrl) return null;
  const label = SOURCE_META[event.source]?.label ?? 'source';
  return {
    href: event.sourceItemUrl,
    ref: event.sourceItemRef ? `#${event.sourceItemRef}` : null,
    title: `Open in ${label}`,
  };
}

/**
 * Build the facts model for an event, or null when the source knows nothing
 * beyond what the card already shows — the row is then absent, not empty.
 */
export function sourceFacts(
  event: SourceEvent | undefined,
  variant: 'card' | 'strip',
): SourceFacts | null {
  if (!event) return null;

  const strip = variant === 'strip';
  const allTags = event.tags ?? [];
  const tags = strip ? allTags : allTags.slice(0, CARD_TAG_CAP);
  const quiet: string[] = [];
  const flags: string[] = [];

  if (strip && event.sourceItemType) quiet.push(event.sourceItemType);

  // Ownership reads first: who called this, how many were in it, does it recur.
  if (strip && event.isOrganizer === true) quiet.push('You organised');
  if (strip && event.isOrganizer === false && event.organizerName) quiet.push(event.organizerName);

  const attendees = event.attendees?.length ?? 0;
  if (attendees > 1) quiet.push(`${attendees} attendees`);
  if (event.isRecurring) quiet.push('Recurring');
  if (strip && event.location) quiet.push(event.location);

  if (event.inviteResponse) {
    const quietResponse = RESPONSE_QUIET[event.inviteResponse];
    const flagResponse = RESPONSE_FLAGS[event.inviteResponse];
    if (quietResponse) quiet.push(quietResponse);
    if (flagResponse) flags.push(flagResponse);
  }

  if (event.availability && AVAILABILITY_FLAGS[event.availability]) {
    flags.push(AVAILABILITY_FLAGS[event.availability]);
  }

  // A judgment the user already made in the source, carried as a chip rather
  // than acted on: whether non-billable time belongs somewhere else is a tenant
  // convention, not ours to apply (ADR-0026 amendment).
  if (strip && event.sourceBillable === false) flags.push('Non-billable');

  if (tags.length === 0 && quiet.length === 0 && flags.length === 0) return null;

  return { tags, tagOverflow: allTags.length - tags.length, quiet, flags };
}
